/**
 * Every word on the landing page, in both languages.
 *
 * Lives in `shared/` for the same reason `compute.js` does: the server serves it
 * from `/api/landing/content` and the client bundles it as the offline fallback,
 * so the two can never drift into saying different things. Editing the page's
 * copy means editing this file and nothing else.
 *
 * Numbers are deliberately absent here — the ticker and the member counter are
 * served separately by `/api/landing/stats` and `/api/landing/discord`, because
 * those change on their own schedule and this file does not.
 */

export const LANGUAGES = ['PL', 'EN'];

export const DEFAULT_LANGUAGE = 'PL';

/** Normalises anything a query string or a stored preference might hold. */
export function normaliseLanguage(value, fallback = DEFAULT_LANGUAGE) {
  const code = String(value || '').trim().toUpperCase();
  if (LANGUAGES.includes(code)) return code;
  // The tracker writes 'Polski'/'English' in a couple of places; accept both.
  if (code === 'POLSKI' || code === 'POLSKA' || code === 'PL-PL') return 'PL';
  if (code === 'ENGLISH' || code.startsWith('EN-')) return 'EN';
  return fallback;
}

export const LANDING_CONTENT = {
  PL: {
    langTitle: 'Zmień język / Change language',
    navTracker: 'Portfolio Tracker',
    navGuides: 'Poradniki',
    navGuidesShort: 'Poradniki',
    guidesHref: '/poradniki/',
    legal: {
      terms: 'Regulamin', termsHref: '/regulamin/',
      privacy: 'Polityka prywatności', privacyHref: '/polityka-prywatnosci/'
    },
    navDiscord: 'DISCORD',
    skipToContent: 'Przejdź do treści',
    docTitle: 'Portfolio Tracker — Twoje finanse w jednym miejscu',
    docDescription: 'Ile naprawdę masz? Portfolio Tracker łączy budżet, długi, inwestycje i koszty auta, a co miesiąc liczy Twoją wartość netto. Podstawowe funkcje za 0 zł.',

    h1a: 'Czytamy wykres.',
    h1b: 'Nie nastroje.',
    heroSub: 'Liquidity Raiders to otwarta grupa dla ludzi, którzy patrzą na rynek przez pryzmat tego, co faktycznie pokazuje wykres. Bez motywacyjnych sloganów — konkretne analizy, strategie i twarde zasady zarządzania ryzykiem.',
    heroSub2: 'Oprócz społeczności oferujemy autorskie narzędzia, które pomogą Ci zarządzać codziennymi wydatkami. Sprawdź już teraz — podstawowy dostęp jest bezpłatny!',
    ctaJoin: 'DOŁĄCZ DO DISCORDA',
    /*
     * Which hero the page opens on: 'community' (Liquidity Raiders first, the
     * tools after) or 'product' (Portfolio Tracker first, the community a line
     * below). The Polish page is the community's home; the English one is
     * where Product Hunt and search send people who came for the tracker.
     */
    heroFocus: 'product',
    phKicker: 'PORTFOLIO TRACKER · OD LIQUIDITY RAIDERS',
    phA: 'Cały obraz Twoich finansów,',
    phB: 'miesiąc po miesiącu.',
    phSub: 'Budżet, długi, inwestycje, auto i\u00a0dziennik tradera — w\u00a0jednym rejestrze, który co miesiąc liczy Twoją wartość netto, w\u00a0Twojej walucie, na każdym urządzeniu.',
    phSub2: 'Cały rejestr jest bezpłatny. Najpierw wypróbuj demo — bez rejestracji i\u00a0bez karty.',
    ctaDemo: 'WYPRÓBUJ DEMO',
    ctaSignUp: 'Załóż darmowe konto',
    ctaDiscordLine: 'Tworzymy go z\u00a0naszą społecznością traderów — dołącz na Discordzie',
    /* Mixed case on purpose: these buttons have no text-transform, so the
       capitals in the copy beside this one are literal, not CSS. */
    ctaOpenTracker: 'Otwórz Portfolio Tracker',

    livePreview: 'ZOBACZ NA ŻYWO',
    /*
     * The heading over the pinned showcase.
     *
     * The stage is a sticky, full-height panel with its two columns centred,
     * which left most of a screen empty above them and nothing saying what the
     * reader was looking at — the panels introduce themselves one at a time,
     * and none of them introduces the section.
     */
    productHeading: 'Poznaj nasze narzędzia',
    productKicker: 'PORTFOLIO TRACKER',
    featuresKicker: 'MOŻLIWOŚCI',
    featuresTitle: 'Wszystko, co składa się na pełny obraz',
    desktopKicker: 'PANEL DESKTOPOWY',
    desktopTitle: 'Twój budżet na wyciągnięcie ręki',
    desktopBody: 'Całość podana jak na tacy. Na ekranie głównym widzisz to, co najważniejsze: wartość netto, miesięczne przepływy, kolejkę spłat, alokację portfela i koszty utrzymania samochodu. Dostęp z każdego Twojego urządzenia.',
    /*
     * Under the live preview: the real app, running on a sample ledger. The
     * switcher walks through its screens on its own until somebody picks one.
     */
    previewNote: 'Prawdziwa aplikacja na przykładowych danych',
    previewTabs: {
      overview: 'Przegląd', cash: 'Przychody i wydatki', debt: 'Długi',
      invest: 'Inwestycje', car: 'Pojazd', journal: 'Dziennik'
    },
    previewFrame: 'Podgląd aplikacji Portfolio Tracker',

    bottomBody: 'Dołącz do naszej społeczności i przekonaj się na własne oczy — kanały z omówieniami rynku i codzienne wiadomości ze świata od bota prasowego Liquidity Raiders.',
    bottomBtn: 'DOŁĄCZ DO SERWERA',
    footerNote: '© 2026 LIQUIDITY RAIDERS',
    onlineSuffix: 'RAIDERS ONLINE',
    membersSuffix: 'RAIDERS',

    panels: [
      {
        title: 'Cały miesiąc w jednym wierszu',
        body: 'Przychody, wydatki i to, co faktycznie zostało. Każdy wpis dodasz dwoma dotknięciami, a stopa oszczędności przeliczy się od razu.',
        tags: ['Przychody', 'Wydatki', 'Stopa oszczędności']
      },
      {
        title: 'Długi ustawione w kolejkę spłat',
        body: 'Kredyty posortowane według oprocentowania albo salda. Widzisz, ile w tym miesiącu kosztują Cię odsetki i po ilu ratach spłacisz każdy z nich.',
        tags: ['Oprocentowanie', 'Kolejka spłat', 'Zapis wpłaty']
      },
      {
        title: 'Twoje inwestycje i koszty utrzymania samochodu — w jednym rejestrze',
        body: 'ETF-y, kryptowaluty i konto emerytalne obok kosztu na kilometr i rejestru tankowań. Wydatki na pojazd trafiają automatycznie do miesięcznego budżetu.',
        tags: ['Alokacja', 'Zysk/strata', 'Koszt na km']
      }
    ],

    features: [
      { title: 'Przejmij kontrolę nad swoimi finansami', body: 'Aktywa minus zobowiązania obliczane miesiąc po miesiącu. Wykres rośnie razem z Twoimi inwestycjami, bez ręcznego przepisywania sald.' },
      { title: 'Wydatki według kategorii', body: 'Czternaście kategorii, od zakupów spożywczych po subskrypcje. Import wyciągu CSV z banku rozpoznaje popularne sklepy i zapamiętuje Twoje poprawki.' },
      { title: 'Plan wyjścia z długów', body: 'Dwie strategie: najwyższe oprocentowanie albo najmniejsze saldo. Zapisujesz wpłatę, saldo spada, rata ląduje w wydatkach.' },
      { title: 'Portfel inwestycyjny', body: 'Niezrealizowany zysk lub strata, koszt zakupu i alokacja według klas aktywów — od ETF-ów przez obligacje po kryptowaluty.' },
      { title: 'Rejestr pojazdu', body: 'Wpisz ostatnie tankowanie, a spalanie policzy się samo. Serwis, ubezpieczenie i rejestrację zapiszesz jako osobne wydatki.' },
      { title: 'Komputer i telefon', body: 'Ten sam rejestr na komputerze i w telefonie. Zmiany synchronizują się same, a bez internetu dalej dodasz wpisy — wyślą się, gdy wróci połączenie.' }
    ],

    guides: {
      kicker: 'PORADNIKI',
      title: 'Odkryj, jakie to proste',
      body: 'Konkretne odpowiedzi na pytania, które pojawiają się przy prowadzeniu własnych finansów — policzone na przykładach, a nie opisane ogólnikami.',
      read: 'CZYTAJ',
      all: 'Wszystkie poradniki',
      items: [
        {
          href: '/poradniki/jak-korzystac-z-portfolio-tracker/',
          main: true,
          title: 'Jak korzystać z Portfolio Tracker: kompletny poradnik',
          body: 'Wszystko o aplikacji w jednym miejscu: konto i demo, każda zakładka krok po kroku, import wyciągów, waluty, plany i bezpieczeństwo danych.',
          meta: 'KOMPLETNY PORADNIK · 31 MIN CZYTANIA'
        },
        {
          href: '/poradniki/lawina-czy-kula-sniezna/',
          title: 'Nadpłata długów: metoda lawiny czy kuli śnieżnej?',
          body: 'Dwie metody na jednym przykładzie: 2 470 zł odsetek kontra 2 586 zł — i dlaczego ważniejsze od metody jest to, że w ogóle nadpłacasz.',
          meta: '6 MIN CZYTANIA'
        },
        {
          href: '/poradniki/jak-obliczyc-spalanie/',
          title: 'Jak obliczyć spalanie auta: metoda od pełnego do pełnego',
          body: 'Wzór, przykład, tankowania częściowe i błąd, który popełnia większość arkuszy kalkulacyjnych.',
          meta: '5 MIN CZYTANIA'
        }
      ]
    },

    /*
     * The price table: Free beside Liquidity+, row by row.
     *
     * Figures are placeholders -- {month}, {journal} and the rest -- filled
     * in by the page from shared/plans.js, the same numbers the app enforces
     * and Stripe charges. A price typed here as well would one day disagree.
     */
    pricing: {
      kicker: 'PLANY',
      title: 'Zacznij za darmo. Rozszerz, kiedy zechcesz.',
      body: 'Cały rejestr finansów jest bezpłatny, razem z importem wyciągów. Liquidity+ jest dla aktywnych traderów i dla tych, którzy nie chcą wpisywać cen ręcznie — i pokrywa realne koszty utrzymania aplikacji: serwera, kopii zapasowych i aktualizacji.',
      feature: 'FUNKCJA',
      yes: 'tak',
      no: 'nie',
      /*
       * Both columns have the same four lines -- name, price, one line under
       * it, a badge -- so each lines up with its neighbour across the table.
       */
      free: { name: 'FREE', price: '{zero}', sub: 'bez limitu czasu', badge: 'BEZ KARTY', cta: 'ZAŁÓŻ KONTO' },
      plus: { name: 'LIQUIDITY+', price: '{month}', unit: '/ mies.', sub: 'albo {year} rocznie', badge: '14 DNI ZA DARMO', cta: 'WYPRÓBUJ ZA DARMO' },
      regionLabel: 'Ceny dla:',
      regions: {
        'eu:PLN': 'Unia Europejska · złoty',
        'eu:EUR': 'Unia Europejska · euro',
        'global:USD': 'USA, Kanada, Australia i inne · dolar',
        'global:GBP': 'Wielka Brytania · funt',
        'global:CHF': 'Szwajcaria · frank',
        'emerging:USD': 'Pozostałe kraje · dolar'
      },
      rows: [
        ['Rejestr finansów: budżet, długi, inwestycje, pojazd', true, true],
        ['Wartość netto i wykresy miesiąc po miesiącu', true, true],
        ['Komputer i telefon, eksport danych', true, true],
        ['Import wyciągów CSV', 'bez limitu', 'bez limitu'],
        ['Dziennik tradera', '{journal} transakcji / mies.', 'bez limitu'],
        ['Backtest strategii', '1 sesja', 'bez limitu'],
        ['Konta prop firm', '1 konto', 'bez limitu'],
        ['Podatki: Polska (PIT-38), USA (futures i\u00a0akcje), Niemcy, Wielka Brytania', false, true],
        ['Automatyczne notowania i historia cen', false, true],
        ['Podpowiedzi kategorii AI (Claude od Anthropic)', false, '{aiRows} opisów / mies.'],
        ['Tygodniowy przegląd od Claude’a', false, true]
      ],
      soon: 'Plany zaczną obowiązywać wkrótce — datę startu ogłosimy co najmniej 14 dni wcześniej. Do tego czasu wszystkie funkcje są dostępne bez opłat.',
      startsOn: 'Plany zaczną obowiązywać {date}. Do tego czasu wszystkie funkcje są dostępne bez opłat.',
      changesOn: 'Limity backtestu i kont prop firm oraz zestawienia podatkowe w Liquidity+ obowiązują od {date}. Import bez limitu już działa.',
      fine: 'Ceny zależą od regionu i są cenami końcowymi. Subskrypcję możesz anulować w każdej chwili, a Twoje dane zostają z Tobą także w planie Free.',
      termsLink: 'Szczegóły w regulaminie',
      termsHref: '/regulamin/'
    },

    /*
     * Labels for the illustrative rows drawn inside the phone and desktop
     * mock-ups. They live here rather than in `preview.js` for one reason: a
     * mock left in Polish on an English page reads as a bug in the product, not
     * as a placeholder.
     */
    mock: {
      spend: [['Mieszkanie', 34], ['Zakupy', 22], ['Media', 15], ['Rozrywka', 11], ['Pojazd', 9]],
      debts: [['Kredyt hipoteczny', '6,2%', 62], ['Karta kredytowa', '18,9%', 24], ['Kredyt na auto', '9,1%', 41]],
      tabs: ['GOTÓWKA', 'DŁUG', 'INWEST.', 'AUTO'],
      netWorth: '184 920'
    },

    waitlist: {
      kicker: 'POWIADOMIENIA',
      title: 'Powiadomimy Cię o nowych funkcjach',
      body: 'Jeden e-mail, kiedy pojawi się coś istotnego: nowa sekcja trackera, aplikacja mobilna, zmiany w dostępie. Bez newslettera i bez spamu.',
      placeholder: 'twoj@email.pl',
      submit: 'ZAPISZ MNIE',
      submitting: 'WYSYŁANIE…',
      consent: 'Zapisując się, zgadzasz się na otrzymanie wiadomości potwierdzającej. Wypisać się można jednym kliknięciem.',
      okTitle: 'Sprawdź skrzynkę',
      okBody: 'Wysłaliśmy link potwierdzający. Kliknij go, żeby dokończyć zapis — bez tego nic nie wyślemy.',
      alreadyTitle: 'Już jesteś na liście',
      alreadyBody: 'Ten adres jest potwierdzony. Nic więcej nie musisz robić.',
      resentBody: 'Ten adres czekał już na potwierdzenie — wysłaliśmy link jeszcze raz.',
      confirmedTitle: 'Zapisano',
      confirmedBody: 'Adres potwierdzony. Odezwiemy się, kiedy będzie o czym pisać.',
      unsubscribedTitle: 'Wypisano',
      unsubscribedBody: 'Nie wyślemy już nic na ten adres.',
      errInvalid: 'To nie wygląda na adres e-mail.',
      errRate: 'Za dużo prób. Spróbuj ponownie za chwilę.',
      errGeneric: 'Nie udało się zapisać. Spróbuj ponownie za chwilę.',
      errLinkDead: 'Link wygasł albo został już użyty. Zapisz się jeszcze raz.',
      /* For a link followed after sign-ups closed: there is nowhere to sign up again. */
      linkDead: 'Ten link wygasł albo został już użyty.'
    }
  },

  EN: {
    langTitle: 'Change language / zmień język',
    navTracker: 'Portfolio Tracker',
    navGuides: 'Guides',
    navGuidesShort: 'Guides',
    guidesHref: '/en/guides/',
    legal: {
      terms: 'Terms of service', termsHref: '/en/terms/',
      privacy: 'Privacy policy', privacyHref: '/en/privacy/'
    },
    navDiscord: 'DISCORD',
    skipToContent: 'Skip to content',
    docTitle: 'Portfolio Tracker — your finances in one place',
    docDescription: 'How much do you really have? Portfolio Tracker brings your budget, debts, investments and car costs together and works out your net worth every month. The basics are free.',

    h1a: 'We read the chart.',
    h1b: 'Not the mood.',
    heroSub: 'Liquidity Raiders is an open group for people who read the market through what the chart actually shows. No motivational slogans — concrete analysis, strategies and disciplined risk management.',
    heroSub2: 'Alongside the community, we build our own tools to help you manage your everyday spending. Try them now — basic access is free!',
    ctaJoin: 'JOIN THE DISCORD',
    heroFocus: 'product',
    phKicker: 'PORTFOLIO TRACKER · BY LIQUIDITY RAIDERS',
    phA: 'Your whole financial picture,',
    phB: 'month by month.',
    phSub: 'Budget, debts, investments, your car and your trading journal — in one ledger that works out your net worth every month, in your own currency, on every device.',
    phSub2: 'The whole ledger is free. Try the demo first — no sign-up, no card.',
    ctaDemo: 'TRY THE DEMO',
    ctaSignUp: 'Create a free account',
    ctaDiscordLine: 'Built with our trading community — join us on Discord',
    ctaOpenTracker: 'Proceed to Portfolio Tracker',

    livePreview: 'LIVE PREVIEW',
    productHeading: 'Discover our tools',
    productKicker: 'PORTFOLIO TRACKER',
    featuresKicker: 'CAPABILITIES',
    featuresTitle: 'Everything that adds up to the full picture',
    desktopKicker: 'DESKTOP PANEL',
    desktopTitle: 'Your budget, on every device',
    desktopBody: 'Everything laid out for you. The home screen shows what matters most: net worth, monthly cashflow, the payoff queue, portfolio allocation and vehicle running costs. Available on every device you own.',
    previewNote: 'The real app, running on sample data',
    previewTabs: {
      overview: 'Overview', cash: 'Income & spend', debt: 'Debt',
      invest: 'Investments', car: 'Vehicle', journal: 'Trade journal'
    },
    previewFrame: 'Portfolio Tracker app preview',

    bottomBody: 'Join our community and see for yourself — channels with market breakdowns and daily world news from the Liquidity Raiders press bot.',
    bottomBtn: 'JOIN THE SERVER',
    footerNote: '© 2026 LIQUIDITY RAIDERS',
    onlineSuffix: 'RAIDERS ONLINE',
    membersSuffix: 'RAIDERS',

    panels: [
      {
        title: 'Your month in a single line',
        body: 'Income, spending and what actually stayed. Every entry takes two taps, and the savings rate recalculates instantly.',
        tags: ['Income', 'Expenses', 'Savings rate']
      },
      {
        title: 'Debts lined up in a payoff queue',
        body: 'Loans sorted by interest rate or by balance. You see what interest costs you this month and how many payments clear each balance.',
        tags: ['APR', 'Payoff queue', 'Log a payment']
      },
      {
        title: 'Your investments and what the car costs to run — in one ledger',
        body: 'ETFs, crypto and your retirement account next to cost per mile and the fuel log. Vehicle spending lands in the monthly budget automatically.',
        tags: ['Allocation', 'Profit & loss', 'Cost per mile']
      }
    ],

    features: [
      { title: 'Take control of your finances', body: 'Assets minus liabilities, worked out month after month. The chart grows with your investments — no rewriting balances by hand.' },
      { title: 'Spending by category', body: 'Fourteen categories, from groceries to subscriptions. A bank statement imported as CSV recognises common shops by itself and remembers your corrections.' },
      { title: 'A way out of debt', body: 'Two strategies: highest interest rate or smallest balance. Log a payment, the balance drops, the instalment lands in expenses.' },
      { title: 'Investment portfolio', body: 'Unrealised gains and losses, cost basis and allocation by asset class — from ETFs through bonds to crypto.' },
      { title: 'Vehicle log', body: 'Log your latest fill-up and fuel consumption works itself out. Servicing, insurance and registration are tracked as separate expenses.' },
      { title: 'Desktop & Mobile', body: 'The same ledger on your computer and your phone. Changes sync on their own, and without a connection you can keep adding entries — they go out when you are back online.' }
    ],

    guides: {
      kicker: 'GUIDES',
      title: 'See how simple it is',
      body: 'Straight answers to the questions that come up when you run your own finances — worked through on real numbers rather than described in generalities.',
      read: 'READ',
      all: 'All guides',
      items: [
        {
          href: '/en/guides/how-to-use-portfolio-tracker/',
          main: true,
          title: 'How to use Portfolio Tracker: the complete guide',
          body: 'Everything about the app in one place: your account and the demo, every tab step by step, statement imports, currencies, plans and data safety.',
          meta: 'THE COMPLETE GUIDE · 38 MIN READ'
        },
        {
          href: '/en/guides/debt-avalanche-vs-snowball/',
          title: 'Paying off debt: the avalanche or the snowball method?',
          body: 'Both methods on one example: $2,470 of interest against $2,586 — and why overpaying at all matters more than the method.',
          meta: '6 MIN READ'
        },
        {
          href: '/en/guides/car-fuel-consumption/',
          title: 'How to work out your car’s real fuel consumption: full to full',
          body: 'The formula, a worked example, partial fills and the mistake most spreadsheets make.',
          meta: '5 MIN READ'
        }
      ]
    },

    /* Złoty here too: it is what the subscription is charged in, whatever the page's language. */
    pricing: {
      kicker: 'PLANS',
      title: 'Start free. Upgrade when you want.',
      body: 'The whole finance ledger is free, statement imports included. Liquidity+ is for active traders and for anyone who would rather not type prices in — and it covers the real cost of running the app: the server, backups and updates.',
      feature: 'FEATURE',
      yes: 'yes',
      no: 'no',
      free: { name: 'FREE', price: '{zero}', sub: 'no time limit', badge: 'NO CARD NEEDED', cta: 'CREATE AN ACCOUNT' },
      plus: { name: 'LIQUIDITY+', price: '{month}', unit: '/ mo', sub: 'or {year} a year', badge: '14 DAYS FREE', cta: 'TRY IT FREE' },
      regionLabel: 'Prices for:',
      regions: {
        'eu:PLN': 'European Union · złoty',
        'eu:EUR': 'European Union · euro',
        'global:USD': 'US, Canada, Australia and more · dollar',
        'global:GBP': 'United Kingdom · pound',
        'global:CHF': 'Switzerland · franc',
        'emerging:USD': 'Other countries · dollar'
      },
      rows: [
        ['Finance ledger: budget, debts, investments, vehicle', true, true],
        ['Net worth and charts, month by month', true, true],
        ['Desktop and phone, data export', true, true],
        ['CSV statement import', 'unlimited', 'unlimited'],
        ['Trading journal', '{journal} trades / mo', 'unlimited'],
        ['Strategy backtesting', '1 session', 'unlimited'],
        ['Prop firm accounts', '1 account', 'unlimited'],
        ['Tax summaries: Poland (PIT-38), US (futures and shares), Germany, UK', false, true],
        ['Automatic prices and price history', false, true],
        ['AI category suggestions (Claude by Anthropic)', false, '{aiRows} descriptions / mo'],
        ['Weekly review by Claude', false, true]
      ],
      soon: 'The plans start soon — we will announce the date at least 14 days ahead. Until then every feature is free.',
      startsOn: 'The plans start on {date}. Until then every feature is free.',
      changesOn: 'The backtesting and prop firm limits, and tax summaries on Liquidity+, apply from {date}. Unlimited imports apply already.',
      fine: 'Prices depend on your region and are final. You can cancel a subscription at any time, and your data stays with you on the Free plan too.',
      termsLink: 'Details in the terms of service',
      termsHref: '/en/terms/'
    },

    mock: {
      spend: [['Housing', 34], ['Groceries', 22], ['Utilities', 15], ['Leisure', 11], ['Vehicle', 9]],
      debts: [['Mortgage', '6.2%', 62], ['Credit card', '18.9%', 24], ['Car loan', '9.1%', 41]],
      tabs: ['CASH', 'DEBT', 'INVEST', 'CAR'],
      netWorth: '184,920'
    },

    waitlist: {
      kicker: 'NOTIFICATIONS',
      title: 'We will tell you when something ships',
      body: 'One email when something actually matters: a new tracker section, the mobile app, a change to access. No newsletter, no filler.',
      placeholder: 'you@email.com',
      submit: 'KEEP ME POSTED',
      submitting: 'SENDING…',
      consent: 'By signing up, you agree to receive one confirmation message. Unsubscribing takes a single click.',
      okTitle: 'Check your inbox',
      okBody: 'We sent a confirmation link. Click it to finish signing up — nothing is sent until you do.',
      alreadyTitle: 'You are already on the list',
      alreadyBody: 'This address is confirmed. There is nothing else to do.',
      resentBody: 'This address was already waiting to be confirmed — we sent the link again.',
      confirmedTitle: 'Signed up',
      confirmedBody: 'Address confirmed. We will write when there is something worth writing about.',
      unsubscribedTitle: 'Unsubscribed',
      unsubscribedBody: 'Nothing further will be sent to this address.',
      errInvalid: 'That does not look like an email address.',
      errRate: 'Too many attempts. Try again shortly.',
      errGeneric: 'That did not go through. Try again shortly.',
      errLinkDead: 'The link expired or was already used. Sign up again.',
      linkDead: 'This link has expired or has already been used.'
    }
  }
};

/**
 * The ticker's fallback row.
 *
 * Served whenever `LANDING_STATS_USER_ID` is unset, which is the default —
 * these are illustrative figures for a marketing strip, not anybody's books.
 * Kept beside the copy because they are read as copy.
 */
export const CURATED_TICKER = {
  PL: [
    { label: 'Wartość netto', value: '+4,7%', tone: 'up' },
    { label: 'Stopa oszczędności', value: '31%', tone: 'up' },
    { label: 'Dług — kolejka spłat', value: '−12 340 zł', tone: 'down' },
    { label: 'Portfel ETF', value: '+18,2%', tone: 'up' },
    { label: 'Koszt na kilometr', value: '0,19 zł', tone: 'accent' },
    { label: 'Raty miesięczne', value: '815 zł', tone: 'muted' }
  ],
  EN: [
    { label: 'Net worth', value: '+4.7%', tone: 'up' },
    { label: 'Savings rate', value: '31%', tone: 'up' },
    { label: 'Debt — payoff queue', value: '−$12,340', tone: 'down' },
    { label: 'ETF portfolio', value: '+18.2%', tone: 'up' },
    { label: 'Cost per mile', value: '$0.31', tone: 'accent' },
    { label: 'Monthly payments', value: '$815', tone: 'muted' }
  ]
};

/** The colour each `tone` renders as. One place, so the two ends agree. */
export const TICKER_TONES = {
  up: '#3DD9A0',
  down: '#EF4E43',
  accent: '#6FE8BC',
  muted: '#8296A8'
};

export function contentFor(language) {
  return LANDING_CONTENT[normaliseLanguage(language)];
}
